import { useEffect, useRef, useState } from "react";
import { authLogin, setAuthToken } from "@/lib/api";

// Minimal Matrix-style lock: black screen, one blinking green caret. No labels.
// Type the password blind and press Enter. Wrong password → red glitch shake.
export const LockScreen = ({ onUnlock }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const focus = () => inputRef.current?.focus();
    focus();
    document.addEventListener("click", focus);
    return () => document.removeEventListener("click", focus);
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    // Il DOM e' la fonte di verita': lo state React puo' non essere ancora
    // committato quando l'Enter arriva subito dopo l'ultimo carattere.
    const pwd = inputRef.current?.value ?? value;
    if (!pwd || busy) return;
    setBusy(true);
    try {
      const { token } = await authLogin(pwd);
      setAuthToken(token);
      onUnlock();
    } catch {
      setError(true);
      setValue("");
      setTimeout(() => setError(false), 650);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-black flex items-center justify-center cursor-text select-none"
      data-testid="lock-screen"
      onClick={() => inputRef.current?.focus()}
    >
      <div className="crt-overlay" aria-hidden="true" />
      <form onSubmit={submit} className="absolute opacity-0 w-px h-px">
        <input
          ref={inputRef}
          type="password"
          autoFocus
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(e); }}
          className="w-px h-px"
          data-testid="lock-input"
          aria-label=""
        />
        <button type="submit" tabIndex={-1} data-testid="lock-submit" aria-hidden="true" />
      </form>
      <div
        aria-hidden="true"
        data-testid="lock-caret-row"
        className={error ? "lock-row lock-row-error" : "lock-row"}
      >
        {Array.from({ length: Math.min(value.length, 32) }).map((_, i) => (
          <span key={i} className={error ? "lock-dot lock-dot-error" : "lock-dot"} />
        ))}
        <span
          data-testid="lock-caret"
          className={error ? "lock-caret lock-caret-error" : "lock-caret"}
        />
      </div>
      <style>{`
        .lock-row {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .lock-row-error {
          animation: lockShake 0.55s ease;
        }
        .lock-dot {
          width: 4px;
          height: 4px;
          border-radius: 9999px;
          background: #33ff66;
          box-shadow: 0 0 3px rgba(51,255,102,0.6);
        }
        .lock-dot-error {
          background: #ff3355;
          box-shadow: 0 0 4px rgba(255,51,85,0.7);
        }
        .lock-caret {
          display: inline-block;
          width: 9px;
          height: 3px;
          background: #33ff66;
          box-shadow: 0 0 4px rgba(51,255,102,0.7);
          animation: lockBlink 1s steps(1) infinite;
        }
        .lock-caret-error {
          background: #ff3355;
          box-shadow: 0 0 5px rgba(255,51,85,0.7);
        }
        @keyframes lockBlink { 0%, 55% { opacity: 1; } 56%, 100% { opacity: 0; } }
        @keyframes lockShake {
          0%, 100% { margin-left: 0; }
          15% { margin-left: -10px; }
          30% { margin-left: 9px; }
          45% { margin-left: -7px; }
          60% { margin-left: 5px; }
          75% { margin-left: -3px; }
        }
      `}</style>
    </div>
  );
};
